import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd, formatDate, shortId } from '../lib/format';
import { STATUS_COLORS } from '../components/SupportCasesTable';

interface SupportCaseResponse {
  case: {
    uuid: string;
    email: string;
    name: string | null;
    subject: string;
    category: string;
    status: string;
    priority: string;
    channel: string;
    created_at: string;
    resolved_at: string | null;
    messages: Array<{
      uuid: string;
      author_type: 'customer' | 'admin';
      body_text: string;
      is_internal: number;
      created_at: string;
      admin_username: string | null;
    }>;
    customer: {
      uuid: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      order_count: number;
      total_spent: number;
    } | null;
    orders: Array<{
      uuid: string;
      status: string;
      payment_status: string;
      total: number;
      created_at: string;
      customer_email: string;
      linked_by: string | null;
    }>;
    recent_orders: Array<{ uuid: string; status: string; total: number; created_at: string }>;
  };
}

export function SupportCase() {
  const { uuid } = useParams();
  const [data, setData] = useState<SupportCaseResponse | null>(null);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get<SupportCaseResponse>(`/api/admin/support/${uuid}`).then(setData).catch(console.error);

  useEffect(() => {
    load();
  }, [uuid]);

  if (!data) return <p>Loading…</p>;
  const sc = data.case;

  const update = async (fields: Record<string, string | null>) => {
    setBusy(true);
    try {
      await api.put(`/api/admin/support/${sc.uuid}`, fields);
      await load();
    } catch (err) {
      alert(`Update failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const linkOrder = async (orderUuid: string) => {
    setBusy(true);
    try {
      await api.post(`/api/admin/support/${sc.uuid}/orders`, { order_uuid: orderUuid });
      await load();
    } catch (err) {
      alert(`Link failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const unlinkOrder = async (orderUuid: string) => {
    setBusy(true);
    try {
      await api.delete(`/api/admin/support/${sc.uuid}/orders/${orderUuid}`);
      await load();
    } catch (err) {
      alert(`Unlink failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const linkedUuids = new Set(sc.orders.map((o) => o.uuid));

  const send = async (status?: string) => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/support/${sc.uuid}/messages`, { body: reply, internal, status });
      setReply('');
      setInternal(false);
      await load();
    } catch (err) {
      alert(`Send failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/support" style={{ color: '#6b7280', fontSize: 13 }}>
        ← Support
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 4px' }}>
        {sc.subject}
      </h1>
      <p style={{ color: '#6b7280', margin: '0 0 24px' }}>
        Case #{shortId(sc.uuid)} · {sc.name ? `${sc.name} · ` : ''}
        {sc.email} · opened {formatDate(sc.created_at)} via {sc.channel}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          {sc.messages.map((m) => (
            <Message key={m.uuid} message={m} customerLabel={sc.name || sc.email} />
          ))}

          <Section title={internal ? 'Internal note' : `Reply to ${sc.email}`}>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={6}
              placeholder={internal ? 'Only visible to admins…' : 'Your reply will be emailed to the customer…'}
              style={{
                width: '100%',
                padding: 10,
                border: `1px solid ${internal ? '#fcd34d' : '#e5e5e5'}`,
                background: internal ? '#fffbeb' : '#fff',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, color: '#6b7280', marginRight: 'auto' }}>
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />{' '}
                Internal note (not emailed)
              </label>
              {!internal && (
                <button onClick={() => send('resolved')} disabled={busy || !reply.trim()} style={btnSecondary}>
                  Send &amp; resolve
                </button>
              )}
              <button onClick={() => send()} disabled={busy || !reply.trim()} style={btnPrimary}>
                {internal ? 'Add note' : 'Send reply'}
              </button>
            </div>
          </Section>
        </div>

        <div>
          <Section title="Case">
            <Field label="Status">
              <select
                value={sc.status}
                disabled={busy}
                onChange={(e) => update({ status: e.target.value })}
                style={{ ...selectStyle, color: STATUS_COLORS[sc.status] }}
              >
                <option value="open">open</option>
                <option value="pending">pending</option>
                <option value="resolved">resolved</option>
                <option value="closed">closed</option>
              </select>
            </Field>
            <Field label="Priority">
              <select
                value={sc.priority}
                disabled={busy}
                onChange={(e) => update({ priority: e.target.value })}
                style={selectStyle}
              >
                <option value="low">low</option>
                <option value="normal">normal</option>
                <option value="high">high</option>
              </select>
            </Field>
            <Row label="Category" value={sc.category} />
            {sc.resolved_at && <Row label="Resolved" value={formatDate(sc.resolved_at)} />}
          </Section>

          <Section title={`Linked orders (${sc.orders.length})`}>
            {sc.orders.length === 0 && <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 8px' }}>None.</p>}
            {sc.orders.map((o) => (
              <div key={o.uuid} style={{ padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Link to={`/orders/${o.uuid}`} style={{ fontFamily: 'monospace' }}>
                    #{shortId(o.uuid)}
                  </Link>
                  <span>{formatUsd(o.total)}</span>
                  <button onClick={() => unlinkOrder(o.uuid)} disabled={busy} style={linkBtn}>
                    Unlink
                  </button>
                </div>
                <div style={{ color: '#6b7280', fontSize: 12 }}>
                  {o.status} · {formatDate(o.created_at)}
                  {o.customer_email !== sc.email && ` · ${o.customer_email}`}
                </div>
              </div>
            ))}
            <OrderSearch exclude={linkedUuids} disabled={busy} onLink={linkOrder} />
          </Section>

          <Section title="Customer">
            {sc.customer ? (
              <>
                <Link to={`/customers/${sc.customer.uuid}`}>
                  {[sc.customer.first_name, sc.customer.last_name].filter(Boolean).join(' ') ||
                    sc.customer.email}
                </Link>
                <Row label="Orders" value={String(sc.customer.order_count)} />
                <Row label="Lifetime spend" value={formatUsd(sc.customer.total_spent)} />
                {sc.recent_orders
                  .filter((o) => !linkedUuids.has(o.uuid))
                  .map((o) => (
                    <div
                      key={o.uuid}
                      style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}
                    >
                      <Link to={`/orders/${o.uuid}`} style={{ fontFamily: 'monospace' }}>
                        #{shortId(o.uuid)}
                      </Link>
                      <span style={{ color: '#6b7280' }}>{o.status}</span>
                      <button onClick={() => linkOrder(o.uuid)} disabled={busy} style={linkBtn}>
                        Link
                      </button>
                    </div>
                  ))}
              </>
            ) : (
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                No purchases from this email yet.
              </p>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

interface OrderSearchResult {
  uuid: string;
  total: number;
  status: string;
  created_at: string;
  customer_email: string;
  customer_name: string;
}

/** Look up any order by number, customer email or name and link it to the case. */
function OrderSearch({
  exclude,
  disabled,
  onLink,
}: {
  exclude: Set<string>;
  disabled: boolean;
  onLink: (orderUuid: string) => void;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<OrderSearchResult[] | null>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .get<{ orders: OrderSearchResult[] }>(`/api/admin/orders?${new URLSearchParams({ q: term, limit: '8' })}`)
        .then((r) => setResults(r.orders))
        .catch(console.error);
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const shown = results?.filter((o) => !exclude.has(o.uuid));

  return (
    <div style={{ marginTop: 12 }}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Find order by #, email or name…"
        style={{ ...selectStyle, width: '100%', padding: '6px 8px', boxSizing: 'border-box' }}
      />
      {shown && shown.length === 0 && (
        <p style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 0' }}>No matching orders.</p>
      )}
      {shown?.map((o) => (
        <div
          key={o.uuid}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 0' }}
        >
          <div style={{ minWidth: 0 }}>
            <Link to={`/orders/${o.uuid}`} style={{ fontFamily: 'monospace' }}>
              #{shortId(o.uuid)}
            </Link>{' '}
            <span style={{ color: '#6b7280' }}>{formatUsd(o.total)}</span>
            <div style={{ color: '#6b7280', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {o.customer_name.trim() || o.customer_email} · {formatDate(o.created_at)}
            </div>
          </div>
          <button
            onClick={() => {
              onLink(o.uuid);
              setQ('');
            }}
            disabled={disabled}
            style={linkBtn}
          >
            Link
          </button>
        </div>
      ))}
    </div>
  );
}

function Message({
  message: m,
  customerLabel,
}: {
  message: SupportCaseResponse['case']['messages'][number];
  customerLabel: string;
}) {
  const isAdmin = m.author_type === 'admin';
  const isInternal = m.is_internal === 1;
  return (
    <div
      style={{
        background: isInternal ? '#fffbeb' : isAdmin ? '#f9fafb' : '#fff',
        border: `1px solid ${isInternal ? '#fcd34d' : '#e5e5e5'}`,
        borderRadius: 8,
        padding: 20,
        marginBottom: 12,
        marginLeft: isAdmin ? 40 : 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: '#374151' }}>
          {isAdmin ? m.admin_username || 'Admin' : customerLabel}
          {isInternal && <span style={{ color: '#b45309', marginLeft: 8 }}>internal note</span>}
        </span>
        <span>{formatDate(m.created_at)}</span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>{m.body_text}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h2
        style={{
          margin: '0 0 12px',
          fontSize: 12,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: '#6b7280',
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 14 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#2563eb',
  fontSize: 13,
  cursor: 'pointer',
};
